import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, ClipboardText, Trash, Eye, UserPlus, UploadSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPE_LABEL = { delete: "perm.deleteRequest", visibility_change: "perm.visibilityChange", registration: "perm.registration", submission: "perm.submission" };
const TYPE_ICON = { delete: Trash, visibility_change: Eye, registration: UserPlus, submission: UploadSimple };
const STATUS_STYLE = { approved: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-600", pending: "bg-amber-50 text-amber-700" };

function RequestRow({ r, onDecide, busy }) {
  const { t } = useTranslation();
  const [role, setRole] = useState(r.payload?.requested_role || "team");
  const Icon = TYPE_ICON[r.type] || ClipboardText;
  const isPending = r.status === "pending";
  return (
    <li data-testid={`approval-row-${r.id}`} className="px-6 py-4 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <Icon size={20} className="text-[#C5A059] mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800">
            {t(TYPE_LABEL[r.type] || "perm.request", r.type)} · <span className="text-slate-500">{t(`perm.recordType.${r.record_type}`, r.record_type)}: {r.record_label || r.record_id}</span>
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {t("perm.requester", "Requester")}: {r.requested_by_name || "—"} · {(r.created_at || "").slice(0, 16).replace("T", " ")}
            {r.reason ? ` · ${t("perm.reason", "Reason")}: ${r.reason}` : ""}
            {r.payload?.visibility ? ` · ${t("perm.newVisibility", "New visibility")}: ${r.payload.visibility}` : ""}
            {!isPending && r.decided_at ? ` · ${t("perm.decidedBy", "Decided by")} ${r.decided_by_name || "—"} · ${(r.decided_at || "").slice(0, 16).replace("T", " ")}` : ""}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!isPending && <span className={`text-xs px-2 py-0.5 rounded-sm ${STATUS_STYLE[r.status] || ""}`} data-testid={`status-${r.id}`}>{t(`perm.${r.status}`, r.status)}</span>}
        {isPending && r.type === "registration" && (
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-32 h-8 rounded-sm bg-white text-xs" data-testid={`approve-role-${r.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {["team", "investor", "admin"].map((x) => <SelectItem key={x} value={x}>{t(`perm.role.${x}`, x)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {isPending && (
          <>
            <Button size="sm" disabled={busy} onClick={() => onDecide(r.id, "approve", r.type === "registration" ? { role } : undefined)} data-testid={`approve-${r.id}`} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle size={15} className="mr-1" />{t("perm.approve", "Approve")}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => onDecide(r.id, "reject")} data-testid={`reject-${r.id}`} className="rounded-sm text-red-600 hover:text-red-700">
              <XCircle size={15} className="mr-1" />{t("perm.reject", "Reject")}
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

export default function Approvals() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending");

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["approvals", tab],
    queryFn: async () => (await api.get("/approvals", { params: { status: tab === "pending" ? "pending" : "all" } })).data,
  });
  const visible = tab === "pending" ? requests : requests.filter((r) => r.status !== "pending");

  const act = useMutation({
    mutationFn: async ({ id, decision, body }) => (await api.post(`/approvals/${id}/${decision}`, body || {})).data,
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success(v.decision === "approve" ? t("perm.approved", "Approved") : t("perm.rejected", "Rejected"));
    },
    onError: (e) => toast.error(e.response?.data?.detail || t("actionFailed", "Action failed")),
  });

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <PageHeader title={t("perm.approvalCentre", "Approval Centre")} subtitle={t("perm.approvalCentreSub", "Review and decide on requests that require Admin approval.")} icon={ClipboardText} />

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
          {["pending", "history"].map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`approvals-tab-${k}`}
              className={`px-3 py-1.5 text-sm rounded-sm ${tab === k ? "bg-[#0B2545] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              {t(`perm.${k}`, k)}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-400" data-testid="approvals-count">{visible.length}</span>
        </div>

        {isLoading ? (
          <p className="px-6 py-8 text-sm text-slate-400">{t("loading", "Loading…")}</p>
        ) : visible.length === 0 ? (
          <p className="px-6 py-10 text-sm text-slate-400 text-center" data-testid="approvals-empty">{t("perm.noRequests", "No pending requests")}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visible.map((r) => (
              <RequestRow key={r.id} r={r} busy={act.isPending} onDecide={(id, decision, body) => act.mutate({ id, decision, body })} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
